// @flow
import { useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CompatibilityContext } from '../../../contexts/compatibility';
import AxiosInstanceFactory from '../../../utils/axiosInstance';
import { apiCompatibilityCheck } from '../../../app_routes/ringBuilder';
import { handleError } from '../../../utils/errors';

type TProps = {
  settingSku: ?string
};

export default function DiamondCompatibility({ settingSku }: TProps) {
  const axiosInstance = AxiosInstanceFactory();
  const { compatibility, setCompatibility } = useContext(CompatibilityContext);
  const dispatch = useDispatch();
  const compatible = compatibility.compatible;
  const recommended = compatibility.recommended;
  const diamondId = useSelector(state => state.ringBuilder.diamond.id);

  const handleClick = useCallback(() => {
    dispatch({ type: 'CLEAR_DYOR_DIAMOND' });
  }, []);

  useEffect(() => {
    if (settingSku && diamondId) {
      // We load this component twice on setting PD page, so we don't want to
      // make a second API call unnecessarily.
      if (
        compatibility.lastCheckedSettingSku === settingSku &&
        compatibility.lastCheckedDiamondId === diamondId
      ) {
        return;
      }

      const updateCompatibility = async () => {
        const res = await axiosInstance.post(apiCompatibilityCheck, {
          setting_sku: settingSku,
          diamond_id: diamondId
        });

        setCompatibility({
          ...res.data,
          lastCheckedSettingSku: settingSku,
          lastCheckedDiamondId: diamondId
        });
      };

      updateCompatibility().catch(handleError);
    } else {
      setCompatibility({ compatible: true });
    }
  }, [settingSku, diamondId]);

  if (compatible) return null;
  // Since there is a setting that will fit, the we will set that as the
  // selected setting when the user chooses "add to diamond".
  if (recommended) return null;

  // Since the setting/diamond are not compatible and there is not a comparable
  // setting that is compatible, we tell the user that they can't add this
  // diamond unless they remove the setting first.
  return (
    <div className='flash-message alert'>
      This setting is not compatible with your chosen diamond. Please{' '}
      <a href='#' onClick={handleClick}>
        delete
      </a>{' '}
      your diamond if you wish to choose this setting.
    </div>
  );
}
